/**
 * Historique du generateur (« Generator history » de Bitwarden, « Password
 * history » de Proton Pass et de Dashlane) : les secrets COPIES depuis le
 * generateur, pour retrouver celui colle dans un formulaire d'inscription
 * quand l'entree n'a jamais ete enregistree.
 *
 * Seule une copie y entre : chaque curseur deplace re-tire un secret, les
 * retenir tous noierait le seul qui a servi. Plafonne en nombre et en age,
 * comme les leaders (Bitwarden : 100 ; Proton Pass : 2 semaines) -- un
 * secret oublie ne doit pas trainer indefiniment, meme chiffre.
 *
 * Au repos : UN blob scelle sous la `vaultKey` (`VaultStore.sealGeneratorHistory`),
 * dans le `localStorage` de cet appareil -- jamais un secret en clair, jamais
 * envoye au serveur (comme chez Bitwarden, l'historique ne se synchronise pas).
 * Un blob d'un autre coffre ou altere ne s'ouvre pas : l'historique repart
 * vide plutot que de faire echouer le deverrouillage.
 */

/** Ce que le generateur a produit. */
export type GeneratedKind = 'password' | 'passphrase' | 'username';

export interface GeneratorHistoryEntry {
  readonly value: string;
  readonly kind: GeneratedKind;
  /** Date de la derniere copie (ms epoch). */
  readonly copiedAt: number;
}

/** Plafond en nombre, celui de Bitwarden. */
export const MAX_GENERATOR_HISTORY = 100;
/** Plafond en age : 30 jours, au-dela d'une inscription inachevee il n'y a plus rien a retrouver. */
export const GENERATOR_HISTORY_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Cle `localStorage` : un blob chiffre, jamais un secret en clair. */
export const GENERATOR_HISTORY_KEY = 'klavox:generator-history';
/** Donnee associee du scellement : un blob de fiche d'entree ou d'envoi ne s'ouvre pas comme historique. */
export const GENERATOR_HISTORY_AAD = 'klavox:generator-history:v1';
/** Un secret plus long n'est pas sorti du generateur (128 caracteres au plus, 20 mots de 9 lettres et separateurs). */
const MAX_VALUE_LENGTH = 1024;

const KINDS: readonly GeneratedKind[] = ['password', 'passphrase', 'username'];

/** Libelle de l'IHM. */
export function generatedKindLabel(kind: GeneratedKind): string {
  return kind === 'passphrase' ? 'Phrase de passe' : kind === 'username' ? 'Identifiant' : 'Mot de passe';
}

/** Retire ce qui a depasse l'age maximal (ou vient du futur : horloge reculee, blob forge) et ce qui depasse le plafond. */
export function pruneGeneratorHistory(history: readonly GeneratorHistoryEntry[], now: number): GeneratorHistoryEntry[] {
  return history
    .filter((entry) => entry.copiedAt <= now && now - entry.copiedAt <= GENERATOR_HISTORY_MAX_AGE_MS)
    .slice(0, MAX_GENERATOR_HISTORY);
}

/**
 * Ajoute une copie en tete. Le meme secret copie deux fois remonte, il n'est
 * pas doublonne. Un secret vide ou demesure n'entre pas.
 */
export function recordGenerated(
  history: readonly GeneratorHistoryEntry[],
  value: string,
  kind: GeneratedKind,
  now: number,
): GeneratorHistoryEntry[] {
  if (value.length === 0 || value.length > MAX_VALUE_LENGTH) return pruneGeneratorHistory(history, now);
  return pruneGeneratorHistory([{ value, kind, copiedAt: now }, ...history.filter((entry) => entry.value !== value)], now);
}

/** Retire la ligne `index` (ordre affiche). Un index hors liste ne change rien. */
export function removeGenerated(history: readonly GeneratorHistoryEntry[], index: number): GeneratorHistoryEntry[] {
  return history.filter((_, position) => position !== index);
}

/**
 * Relit le contenu dechiffre : seules les lignes bien formees survivent,
 * triees de la plus recente a la plus ancienne, puis plafonnees.
 */
export function normalizeGeneratorHistory(raw: unknown, now: number): GeneratorHistoryEntry[] {
  if (!Array.isArray(raw)) return [];
  const entries: GeneratorHistoryEntry[] = [];
  const seen = new Set<string>();
  for (const candidate of raw) {
    if (typeof candidate !== 'object' || candidate === null) continue;
    const { value, kind, copiedAt } = candidate as Record<string, unknown>;
    if (typeof value !== 'string' || value.length === 0 || value.length > MAX_VALUE_LENGTH) continue;
    if (!KINDS.includes(kind as GeneratedKind)) continue;
    if (typeof copiedAt !== 'number' || !Number.isSafeInteger(copiedAt)) continue;
    if (seen.has(value)) continue;
    seen.add(value);
    entries.push({ value, kind: kind as GeneratedKind, copiedAt });
  }
  return pruneGeneratorHistory(
    entries.sort((a, b) => b.copiedAt - a.copiedAt),
    now,
  );
}

/** Ce que l'historique attend du coffre ouvert : sceller et ouvrir, la `vaultKey` reste dans `VaultStore`. */
export interface GeneratorHistoryVault {
  sealGeneratorHistory(history: readonly GeneratorHistoryEntry[]): string;
  openGeneratorHistory(blob: string): unknown;
}

export type GeneratorHistoryStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Relit l'historique de cet appareil. Absent, illisible, d'un autre coffre ou altere : vide. */
export function loadGeneratorHistory(storage: GeneratorHistoryStorage, vault: GeneratorHistoryVault, now: number): GeneratorHistoryEntry[] {
  const blob = storage.getItem(GENERATOR_HISTORY_KEY);
  if (blob === null || blob.length === 0) return [];
  try {
    return normalizeGeneratorHistory(vault.openGeneratorHistory(blob), now);
  } catch {
    return [];
  }
}

/** Ecrit l'historique, scelle ; vide, la cle disparait (rien a ouvrir, pas meme un blob vide). */
export function saveGeneratorHistory(
  storage: GeneratorHistoryStorage,
  vault: GeneratorHistoryVault,
  history: readonly GeneratorHistoryEntry[],
): void {
  if (history.length === 0) {
    storage.removeItem(GENERATOR_HISTORY_KEY);
    return;
  }
  storage.setItem(GENERATOR_HISTORY_KEY, vault.sealGeneratorHistory(history));
}
